import React, { useEffect, useRef, useState } from "react";
import {
    CircleContainer,
    CircleLabel,
    CirclePercentDisplay,
    ProgressCircle,
} from "./CircleAnalyticsStyle";
import { getIntervalAmount } from "../../Functions/incrementAmount";

const CircleDefaults = {
    xOffset: 100,
    yOffset: 100,
    radius: 50,
    percentage: 20,
    gradientStart: "#ff00ee",
    gradientEnd: "#5eaefd",
    animationTime: 1,
    circleLineWidth: 10,
    percentTopOffset: 0,
    percentLeftOffset: 20,
    percentOffestTuning: 1.1,
    label: "",
    labelFontSize: 16,
    labelFontWeight: 600,
    fontColor: "#000000",
};

interface AnalyticCircleProps {
    xOffset: number;
    yOffset: number;
    radius?: number;
    percentage?: number;
    gradientStart?: string;
    gradientEnd?: string;
    animationTime?: number;
    circleLineWidth?: number;
    percentTopOffset?: number;
    percentLeftOffset?: number;
    percentOffestTuning?: number;
    label?: string;
    labelFontSize?: number;
    labelFontWeight?: number;
    fontColor?: string;
}

const percentToOffset = (percent: number) => {
    return 400 - percent * 3.1;
};

export const AnalyticCircle: React.FC<AnalyticCircleProps> = ({
    xOffset,
    yOffset,
    radius = CircleDefaults.radius,
    percentage = CircleDefaults.percentage,
    gradientStart = CircleDefaults.gradientStart,
    gradientEnd = CircleDefaults.gradientEnd,
    animationTime = CircleDefaults.animationTime,
    circleLineWidth = CircleDefaults.circleLineWidth,
    percentTopOffset = CircleDefaults.percentTopOffset,
    percentOffestTuning = CircleDefaults.percentOffestTuning,
    percentLeftOffset = CircleDefaults.percentLeftOffset,
    label = CircleDefaults.label,
    labelFontSize = CircleDefaults.labelFontSize,
    labelFontWeight = CircleDefaults.labelFontWeight,
    fontColor = CircleDefaults.fontColor,
}) => {
    const labelWidthRef = useRef<HTMLDivElement>(null);
    const percentWidthRef = useRef<HTMLDivElement>(null);

    const [currPercentage, setCurrPercentage] = useState<number>(0);

    const [strokeDashOffsetEnd, setStrokeDashOffsetEned] = useState<number>(
        percentToOffset(percentage)
    );
    const [offsetNeeded, setOffsetNeeded] = useState(0);
    const [mobileOffsetRight, setMobileOffsetRight] = useState(0);
    const [mobileOffsetLeft, setMobileOffsetLeft] = useState(0);

    const intervalGap = 50;
    const incremAmount = getIntervalAmount({
        propsPercent: percentage,
        defaultPercent: CircleDefaults.percentage,
        propsAnimationTime: animationTime,
        defaultAnimationTime: CircleDefaults.animationTime,
        intervalAmount: 50,
    });

    useEffect(() => {
        setCurrPercentage(0);
        setStrokeDashOffsetEned(percentToOffset(percentage));
    }, [percentage]);

    useEffect(() => {
        const percentageLoop = setInterval(() => {
            const tempCurrPercentage = currPercentage
                ? currPercentage + incremAmount / 100
                : 0 + incremAmount / 100;
            setCurrPercentage(
                tempCurrPercentage > 100 ? 100 : tempCurrPercentage
            );
        }, intervalGap);
        if (currPercentage ? currPercentage >= percentage : 0 >= percentage) {
            clearInterval(percentageLoop);
        }

        return () => {
            clearInterval(percentageLoop);
        };
    }, [currPercentage, percentage, incremAmount]);

    useEffect(() => {
        setOffsetNeeded(
            labelWidthRef.current && percentWidthRef.current
                ? xOffset +
                      percentWidthRef.current.offsetWidth -
                      labelWidthRef.current.offsetWidth
                : 0
        );
    }, [labelWidthRef, xOffset, percentWidthRef]);

    useEffect(() => {
        setMobileOffsetRight(
            percentWidthRef.current ? percentWidthRef.current.offsetWidth : 0
        );
        setMobileOffsetLeft(
            labelWidthRef.current && percentWidthRef.current
                ? xOffset -
                      labelWidthRef.current.offsetWidth +
                      percentWidthRef.current?.offsetWidth
                : 0
        );
    }, [percentWidthRef, labelWidthRef, xOffset]);

    return (
        <CircleContainer>
            <CircleLabel
                ref={labelWidthRef}
                $fontSize={labelFontSize}
                $fontWeight={labelFontWeight}
                $fontColor={fontColor}
                $paddLeft={offsetNeeded}
                $mobilePadRight={mobileOffsetRight}
                $mobilePadLeft={mobileOffsetLeft}
            >
                {label}
            </CircleLabel>
            <svg width={xOffset * 2} height={yOffset * 2}>
                <defs>
                    <linearGradient
                        id="gradient"
                        x1="100%"
                        x2="0%"
                        y1="20%"
                        y2="20%"
                    >
                        <stop offset="0%" stopColor={gradientStart} />
                        <stop offset="100%" stopColor={gradientEnd} />
                    </linearGradient>
                </defs>
                <ProgressCircle
                    cx={xOffset}
                    cy={yOffset}
                    r={radius}
                    strokeLinecap="round"
                    stroke="url(#gradient)"
                    $strokeDashOffsetEnd={strokeDashOffsetEnd}
                    $animationTime={animationTime}
                    $circleLineWidth={circleLineWidth}
                />
            </svg>
            <CirclePercentDisplay
                ref={percentWidthRef}
                $topOffset={percentTopOffset}
                $leftOffset={-xOffset * percentOffestTuning}
                $fontColor={fontColor}
            >
                {Math.round(currPercentage)}%
            </CirclePercentDisplay>
        </CircleContainer>
    );
};
